/* What the shell needs from the welcome feature (see docs/core-requests.md, "billing/welcome web"):

   - `WELCOME_ROUTE` ('/welcome'): RequireAuth already redirects signed-out users there. Mount this
     feature's screen for '/welcome' and '/welcome/*' (it is `public` and `bare`).
   - `welcomeAlias(pathname, search)`: URLs the API sends people to (Google callback for new accounts
     '/onboarding/age?next=', Google errors '/sign-in?error=', reset email '/reset-password?token=')
     mapped into this flow. Render `<Navigate to={alias} replace />` for them.
   - `onboardingRedirect(user, onboarding)` / `useOnboardingGate()`: after sign-in, an account that
     still needs its date of birth (Google) or the required usage-mode step is sent back to the
     step it left (resumable on any device). */
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../../app/session';
import type { OnboardingState, OnboardingStepId } from '../billing/types';
import { useWelcomeBackend } from './backend';

export const WELCOME_ROUTE = '/welcome';

export const STEP_PATH: Record<OnboardingStepId, string> = {
  account: '/welcome/signup',
  age: '/welcome/onboarding/age',
  'usage-mode': '/welcome/onboarding/usage',
  'connect-apps': '/welcome/onboarding/apps',
  'first-run': '/welcome/onboarding/start',
};
export const WELCOME_PATHS = {
  landing: WELCOME_ROUTE,
  signup: STEP_PATH.account,
  signin: '/welcome/signin',
  forgot: '/welcome/forgot',
  reset: '/welcome/reset',
  verify: '/welcome/verify',
} as const;

const ALIASES: Record<string, string> = {
  '/onboarding': STEP_PATH.age,
  '/onboarding/age': STEP_PATH.age,
  '/sign-in': WELCOME_PATHS.signin,
  '/signin': WELCOME_PATHS.signin,
  '/signup': WELCOME_PATHS.signup,
  '/sign-up': WELCOME_PATHS.signup,
  '/reset-password': WELCOME_PATHS.reset,
};

/** The welcome-flow path for a URL the API or an old link uses, or null when it is not one. */
export function welcomeAlias(pathname: string, search = ''): string | null {
  const to = ALIASES[pathname.replace(/\/+$/, '') || '/'];
  return to ? `${to}${search}` : null;
}

/** Where a signed-in account must go before using the app, or null when onboarding allows it. */
export function onboardingRedirect(user: { profileComplete?: boolean } | null | undefined, onboarding?: OnboardingState | null): string | null {
  if (!user) return null;
  if (user.profileComplete === false) return STEP_PATH.age;
  if (!onboarding || onboarding.finished) return null;
  const missing = onboarding.steps.find((s) => s.required && s.status !== 'done');
  return missing ? STEP_PATH[missing.id] : null;
}

/** For RequireAuth: `{checking, redirect}`; never blocks on an unreachable onboarding route. */
export function useOnboardingGate(): { checking: boolean; redirect: string | null } {
  const session = useSession();
  const backend = useWelcomeBackend();
  const enabled = session.status === 'authenticated';
  const q = useQuery({ queryKey: ['onboarding'], queryFn: () => backend.onboarding(), enabled, staleTime: 60_000, retry: false });
  if (!enabled) return { checking: false, redirect: null };
  if (q.isPending) return { checking: true, redirect: null };
  if (q.isError || !q.data) return { checking: false, redirect: null };
  // The API marks the age step done only once the profile has a date of birth, so the steps say it all.
  return { checking: false, redirect: onboardingRedirect({}, q.data.onboarding) };
}
